import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import type { ObjectReferenceSummary, ReferenceState } from '@heidi/agents';
import { useProject, useWorkflow } from '@heidi/agents/data';
import { useCollection, useDocumentDownloadUrl } from '@heidi/evidence/data';
import { useIdentityEditorIntl } from '@heidi/patients';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { useQueryDocumentById, useQuerySessionById } from '@heidi/session/data';
import { useQueryClinicalTaskById } from '@heidi/tasks/data';
import { isFormTemplateCategory, useQueryTemplateById } from '@heidi/templates/data';
import { useIntl } from 'react-intl';

import {
  referenceLabels,
  referenceSummaries,
  sessionAvatarSeed,
  sessionPatientName,
} from './chat-object-reference-label';

type ReferenceQuery = {
  readonly isPending: boolean;
  readonly fetchStatus: 'fetching' | 'paused' | 'idle';
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => unknown;
};

/** What one kind's query resolved to; the chip and the composer hover card read it. */
export type ObjectReference = {
  readonly query: ReferenceQuery;
  readonly label: string | undefined;
  readonly summary?: ObjectReferenceSummary;
  readonly patientName?: string;
  readonly avatarSeed?: string;
};

export type TemplateReference = ObjectReference & { readonly formTemplate: boolean };

export type TaskReference = ObjectReference & { readonly sessionId?: string };

export const usePatientReference = (id: string) => {
  const intl = useIntl();
  const identity = useIdentityEditorIntl();
  const { isAuthenticated } = useIsAuthenticated();
  const query = useQueryPatientProfileById(id, { isAuthenticated });
  const label = referenceLabels.patient(query.data);
  return {
    query,
    label,
    summary:
      label && query.data
        ? referenceSummaries.patient(label, query.data, intl, identity)
        : undefined,
  };
};

export const useSessionReference = (id: string) => {
  const intl = useIntl();
  const query = useQuerySessionById({ sessionId: id });
  const { data } = query;
  return {
    query,
    label: referenceLabels.session(data, intl),
    summary: data ? referenceSummaries.session(data, intl) : undefined,
    patientName: sessionPatientName(data),
    avatarSeed: sessionAvatarSeed(data),
  };
};

export const useDocumentReference = (id: string, sessionId: string) => {
  const { isFreeUser } = useIsFreeUser();
  const query = useQueryDocumentById({ sessionId, documentId: id, isFreeUser });
  const label = referenceLabels.document(query.data);
  return {
    query,
    label,
    summary:
      label && query.data ? referenceSummaries.document(label, query.data) : undefined,
  };
};

export const useTemplateReference = (id: string) => {
  const intl = useIntl();
  const query = useQueryTemplateById(id, null);
  const label = referenceLabels.template(query.data);
  return {
    query,
    label,
    summary:
      label && query.data
        ? referenceSummaries.template(label, query.data, intl)
        : undefined,
    formTemplate: isFormTemplateCategory(query.data?.template_category),
  };
};

export const useRoutineReference = (id: string) => {
  const { isAuthenticated } = useIsAuthenticated();
  const query = useWorkflow({ workflowId: id, enabled: isAuthenticated });
  return { query, label: referenceLabels.routine(query.data) };
};

export const useProjectReference = (id: string) => {
  const query = useProject(id);
  return { query, label: referenceLabels.project(query.data) };
};

export const useTaskReference = (id: string): TaskReference => {
  const intl = useIntl();
  const { isAuthenticated } = useIsAuthenticated();
  // The hook is not auth-gated itself.
  const query = useQueryClinicalTaskById(isAuthenticated ? id : null);
  const { data } = query;
  const label = referenceLabels.task(data);
  return {
    query,
    label,
    summary: label && data ? referenceSummaries.task(label, data, intl) : undefined,
    sessionId: data?.session_id ?? undefined,
  };
};

export const useFileReference = (id: string) => {
  const { isAuthenticated } = useIsAuthenticated();
  const query = useDocumentDownloadUrl(isAuthenticated ? id : null);
  return { query, label: referenceLabels.file(query.data) };
};

export const useCollectionReference = (id: string) => {
  const intl = useIntl();
  const query = useCollection(id);
  const label = referenceLabels.collection(query.data);
  return {
    query,
    label,
    summary:
      label && query.data
        ? referenceSummaries.collection(label, query.data, intl)
        : undefined,
  };
};

const httpStatus = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null
    ? (error as { response?: { status?: number } }).response?.status
    : undefined;

export const toReferenceState = (
  { query, label, summary, patientName, avatarSeed }: ObjectReference,
  onOpen?: () => void
): ReferenceState => {
  const status = query.isError ? httpStatus(query.error) : undefined;
  if (status === 403 || status === 404) return { status: 'unavailable' };
  if (label)
    return { status: 'resolved', label, onOpen, summary, patientName, avatarSeed };
  if (query.isError) return { status: 'error', retry: () => void query.refetch() };
  // A disabled query stays pending forever without fetching; that is not "loading".
  if (query.isPending && query.fetchStatus !== 'idle') return { status: 'loading' };
  return { status: 'unavailable' };
};
